/**
 * Wire + view models for the RAG ask widget.
 *
 * These mirror the backend projection exposed by `POST {apiUrl}{askPath}`
 * (api/models/generation/response_llm.py). They are intentionally free of any
 * host-application types so the widget can ship as a standalone package.
 */

export type MessageRole = 'user' | 'assistant' | 'system';
export type MessageStatus = 'idle' | 'streaming' | 'done' | 'error';

/**
 * Freshness of a source document, computed server-side from `date_updated`
 * (falling back to `date_created`) against the backend's staleness threshold.
 */
export type Freshness = 'fresh' | 'stale' | 'unknown';

/** One retrieved source backing an answer. */
export interface AskSource {
  title: string;
  source: string;
  link: string;
  score: number;
  date_created: string | null;
  date_updated: string | null;
  freshness: Freshness;
  /** Markdown extract of the chunk used for the answer. Empty when unavailable. */
  extract_md: string;
}

/** A chat turn as rendered by the widget. */
export interface ChatMessage {
  readonly id: string;
  readonly role: MessageRole;
  content: string;
  readonly timestamp: Date;
  status: MessageStatus;
  sources?: AskSource[];
}

/** Response body of the non-streaming ask endpoint. */
export interface AskApiResponse {
  query?: string;
  condensed_query?: string;
  answer: string;
  model?: string;
  sources?: AskSource[];
  chunks_used?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  tokens_per_sec?: number;
  elapsed_sec?: number;
}

/**
 * One frame of a streaming ask response (SSE `data:` payload or NDJSON line).
 * Supported so a host can point the widget at a token-streaming endpoint
 * without changing any widget code.
 */
export interface AskStreamChunk {
  /** Incremental text to append. */
  delta?: string;
  /** Full answer so far — replaces the accumulated text when present. */
  answer?: string;
  sources?: AskSource[];
  done?: boolean;
  error?: string;
}

/** Request body sent to the ask endpoint. */
export interface AskRequestBody {
  query: string;
  collection?: string | number;
  history?: AskHistoryMessage[];
  [key: string]: unknown;
}

export interface AskHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** A selectable document collection to scope the question to. */
export interface RagCollectionRef {
  id: string | number;
  name: string;
}

/**
 * Server-usable snapshot of the conversation: drops system turns, turns still
 * streaming (partial text) and failed turns (their content is UI error text).
 */
export function toAskHistory(messages: readonly ChatMessage[], maxTurns?: number): AskHistoryMessage[] {
  const history = messages
    .filter(
      (m): m is ChatMessage & { role: 'user' | 'assistant' } =>
        (m.role === 'user' || m.role === 'assistant') &&
        m.status === 'done' &&
        m.content.trim().length > 0,
    )
    .map((m) => ({ role: m.role, content: m.content }));

  if (maxTurns && maxTurns > 0 && history.length > maxTurns * 2) {
    return history.slice(-maxTurns * 2);
  }
  return history;
}

export function createMessage(
  role: MessageRole,
  content = '',
  status: MessageStatus = 'done',
): ChatMessage {
  return {
    id: newId(),
    role,
    content,
    timestamp: new Date(),
    status,
  };
}

/** `crypto.randomUUID` is unavailable on insecure origins; fall back gracefully. */
function newId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `rag-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
